import React, { useCallback, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import IconButton from '@material-ui/core/IconButton';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    WithStyles,
    createStyles,
    withStyles,
} from '@material-ui/core';

import { AvailableRolloutPolicy, ExtensionUrl } from 'Console/data/applicationDetail/settings/types';

const styles = createStyles({
    listItem: {
        width: '65%',
        '& span': {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        },
    },
});

interface OriginUrlsFormProps extends WithStyles<typeof styles> {
    initialValue: ExtensionUrl[];
    availableRolloutPolicies: AvailableRolloutPolicy[];
    onChange: (value: ExtensionUrl[]) => void;
}

const OriginUrlsForm = ({
    initialValue = [],
    availableRolloutPolicies = [],
    onChange,
    classes,
}: OriginUrlsFormProps): React.ReactElement => {
    const intl = useIntl();

    const [value, setValue] = useState('');
    const [list, setList] = useState(initialValue);

    const handleOnChange = useCallback((event) => {
        setValue(event.target.value);
    }, []);

    useEffect(() => {
        setList(initialValue);
    }, [initialValue]);

    const handleOnAdd = useCallback(() => {
        const newValue = { url: value, rolloutPolicyId: undefined };
        const newList = [newValue, ...list];

        setList(newList);
        onChange(newList);

        setValue('');
    }, [list, onChange, value]);

    const handleOnDelete = useCallback(
        (indexToDelete) => () => {
            const newList = list.filter((_, index) => index !== indexToDelete);

            setList(newList);
            onChange(newList);
        },
        [list, onChange]
    );

    const handleOnRolloutPolicyChange = useCallback(
        (event, extensionUrl) => {
            const updatedList = list.map((item) =>
                item.url === extensionUrl.url
                    ? {
                          ...item,
                          rolloutPolicyId:
                              event.target.value === 'none' ? undefined : event.target.value,
                      }
                    : item
            );

            setList(updatedList);
            onChange(updatedList);
        },
        [list, onChange]
    );

    const isRolloutPolicyDisabled = (extensionUrl: ExtensionUrl): boolean =>
        extensionUrl.rolloutPolicyId
            ? !availableRolloutPolicies.map((arp) => arp.id).includes(extensionUrl.rolloutPolicyId)
            : false;

    return (
        <Grid container>
            <Grid item xs={12}>
                <Grid container direction="row" alignItems="center">
                    <Grid id="yeself-textField" item xs={8}>
                        <TextField
                            fullWidth
                            type="text"
                            value={value}
                            onChange={handleOnChange}
                            placeholder={intl.formatMessage({ id: 'Settings.general.label2' })}
                        />
                    </Grid>
                    <Grid item xs={4} container justify="flex-end">
                        <Button
                            id="yeself-button"
                            variant="contained"
                            color="primary"
                            onClick={handleOnAdd}
                            disabled={!value}
                        >
                            <FormattedMessage id="Buttons.add" defaultMessage="Add" />
                        </Button>
                    </Grid>
                    <Typography variant="caption">
                        <FormattedMessage
                            id="Settings.general.extensionUrls.message"
                            defaultMessage="* Updating extension URLs might take up to few minutes to have an effect."
                        />
                    </Typography>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <List>
                    {list.map((extensionUrl, index) => (
                        <ListItem key={extensionUrl.url} disableGutters className={classes.listItem}>
                            <ListItemText primary={extensionUrl.url} title={extensionUrl.url}/>
                            <ListItemSecondaryAction>
                                <IconButton
                                    aria-label="Delete"
                                    onClick={handleOnDelete(index)}
                                    disabled={isRolloutPolicyDisabled(extensionUrl)}
                                >
                                    <CancelPresentationIcon />
                                </IconButton>
                                <span
                                    style={{
                                        minWidth: 210,
                                        display: 'inline-flex',
                                        textAlign: 'end',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    {isRolloutPolicyDisabled(extensionUrl) ? (
                                        <Typography variant="caption" inline align="center">
                                            <FormattedMessage
                                                id="Settings.general.inaccessible"
                                                defaultMessage="Inaccessible"
                                            />
                                        </Typography>
                                    ) : (
                                        <FormControl>
                                            <InputLabel
                                                shrink
                                                htmlFor={`rollout-policy-select-${extensionUrl.url}`}
                                                variant="filled"
                                            >
                                                <FormattedMessage
                                                    id="Settings.general.rolloutPolicy"
                                                    defaultMessage="Rollout Policy"
                                                />
                                            </InputLabel>
                                            <Select
                                                value={extensionUrl.rolloutPolicyId ?? 'none'}
                                                style={{ width: 200 }}
                                                inputProps={{
                                                    id: `rollout-policy-select-${extensionUrl.url}`,
                                                }}
                                                onChange={(e) =>
                                                    handleOnRolloutPolicyChange(e, extensionUrl)
                                                }
                                            >
                                                <MenuItem value="none" key="blank-key">
                                                    <FormattedMessage
                                                        id="Settings.general.extensionUrls.options.notSet"
                                                        defaultMessage="(Not set)"
                                                    />
                                                </MenuItem>
                                                {availableRolloutPolicies.map((arp) => (
                                                    <MenuItem value={arp.id} key={arp.id}>
                                                        {arp.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </span>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(OriginUrlsForm);
